'use client';

import { useState, useRef, useEffect } from 'react';
import { Bot, Send, ThumbsUp, ThumbsDown, Loader2, AlertCircle, BookOpen, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';


/**
 * AskPastorWidget Component
 * OpenAI-powered RAG assistant that answers questions using only Christ Faculty content
 * 
 * Features:
 * - Real OpenAI API integration
 * - Semantic search over Christ Faculty content
 * - Source citation with links
 * - Conversation history
 * - Follow-up question suggestions
 * - Loading states and error handling
 */
const AskPastorWidget = () => {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      type: 'ai',
      content: 'Welcome! I\'m Ask Pastor, powered by OpenAI and grounded in Christ Faculty teachings. I can help answer questions about faith, prayer, biblical guidance, and spiritual matters using our library of books, devotionals, messages, and articles.\n\nHow can I assist you today?',
      timestamp: new Date(),
      sources: []
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Quick question suggestions
  const quickQuestions = [
    'How can I grow in my faith?',
    'What does the Bible say about prayer?',
    'How do I overcome anxiety?',
    'What is God\'s purpose for my life?'
  ];

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (questionText = null) => {
    const question = questionText || inputMessage?.trim();
    
    if (!question) return;

    // Clear error state
    setError(null);

    // Add user message
    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: question,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Call Ask Pastor API
      const response = await fetch('/api/ask-pastor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question,
          conversationId,
          includeFollowUp: true
        }),
      });

      const data = await response?.json();

      if (!response?.ok) {
        // Handle specific error codes
        let errorContent = data?.userMessage || data?.error || 'Failed to get response from Ask Pastor';
        
        // Special handling for quota exceeded
        if (data?.code === 'QUOTA_EXCEEDED') {
          errorContent = '⚠️ Ask Pastor is currently unavailable due to high usage.\n\nThis is a temporary issue - our AI service has reached its daily usage limit. Our team has been notified and is working to resolve this.\n\n📝 In the meantime, you can:\n• Browse our Digital Books Library\n• Explore Devotionals section\n• Contact Pastor Denis directly\n\nPlease try again in a few hours. We apologize for the inconvenience!';
        } else if (data?.code === 'RATE_LIMIT') {
          errorContent = '⏱️ Please slow down - you\'re asking questions too quickly.\n\nWait a moment and try again.';
        } else if (data?.code === 'AUTH_ERROR' || data?.code === 'CONFIGURATION_ERROR') {
          errorContent = '🔧 Ask Pastor is experiencing technical difficulties.\n\nOur team has been notified. Please contact support if this persists.';
        }
        
        throw new Error(errorContent);
      }

      // Update conversation ID
      if (data?.conversationId && !conversationId) {
        setConversationId(data?.conversationId);
      }

      // Add AI response
      const aiMessage = {
        id: `ai-${Date.now()}`,
        type: 'ai',
        content: data?.answer,
        timestamp: new Date(),
        sources: data?.sources || [],
        followUpQuestions: data?.followUpQuestions || [],
        metadata: data?.metadata
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err?.message);
      
      // Add error message with proper formatting
      const errorMessage = {
        id: `error-${Date.now()}`,
        type: 'error',
        content: err?.message || 'I apologize, but I encountered an error processing your question. Please try again, or contact our support team if the issue persists.',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedback = async (messageId, feedbackType) => {
    console.log(`Feedback ${feedbackType} for message ${messageId}`);
    // TODO: Implement feedback tracking API
  };

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
    handleSendMessage(question);
  };

  const renderMessage = (message) => {
    if (message?.type === 'user') {
      return (
        <div key={message?.id} className="flex justify-end">
          <div className="max-w-xs lg:max-w-md px-4 py-2 rounded-lg bg-[#4B3381] text-white">
            <p className="text-sm">{message?.content}</p>
            <p className="text-xs text-white/70 mt-1">
              {format(message?.timestamp, 'h:mm a')}
            </p>
          </div>
        </div>
      );
    }

    if (message?.type === 'error') {
      return (
        <div key={message?.id} className="flex justify-start">
          <div className="max-w-xs lg:max-w-md px-4 py-2 rounded-lg bg-red-50 border border-red-200">
            <div className="flex items-center space-x-2 mb-2 text-red-800">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Error</span>
            </div>
            <p className="text-sm text-red-700">{message?.content}</p>
          </div>
        </div>
      );
    }

    // AI message
    return (
      <div key={message?.id} className="flex justify-start">
        <div className="max-w-xs lg:max-w-md px-4 py-2 rounded-lg bg-white border border-gray-200">
          <div className="flex items-center space-x-2 mb-2">
            <Bot className="w-4 h-4 text-[#4B3381]" />
            <span className="text-sm font-medium text-[#4B3381]">Ask Pastor</span>
          </div>
          
          <div className="text-sm text-gray-800 whitespace-pre-wrap">{message?.content}</div>
          
          {/* Sources */}
          {message?.sources && message?.sources?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                <BookOpen className="w-3 h-3 mr-1" />
                Sources (Christ Faculty Content):
              </p>
              {message?.sources?.map((source, index) => (
                <div key={index} className="mb-2 last:mb-0">
                  <a 
                    href={source?.slug ? `/source/${source?.type}/${source?.slug}` : '#'}
                    className="text-xs text-[#4B3381] hover:underline flex items-center group"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="font-medium">{source?.title}</span>
                    {source?.slug && <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </a>
                  <p className="text-xs text-gray-500 mt-1">{source?.snippet}</p>
                  <span className="text-xs text-gray-400 capitalize">{source?.type}</span>
                </div>
              ))}
            </div>
          )}

          {/* Follow-up Questions */}
          {message?.followUpQuestions && message?.followUpQuestions?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-700 mb-2">Related questions:</p>
              {message?.followUpQuestions?.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickQuestion(question)}
                  className="block text-xs text-[#4B3381] hover:bg-[#4B3381]/10 px-2 py-1 rounded mb-1 text-left w-full"
                >
                  → {question}
                </button>
              ))}
            </div>
          )}
          
          {/* Feedback buttons */}
          <div className="flex items-center space-x-2 mt-2">
            <button 
              onClick={() => handleFeedback(message?.id, 'thumbs_up')}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Helpful answer"
            >
              <ThumbsUp className="w-3 h-3 text-gray-400 hover:text-green-600" />
            </button>
            <button 
              onClick={() => handleFeedback(message?.id, 'thumbs_down')}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Not helpful"
            >
              <ThumbsDown className="w-3 h-3 text-gray-400 hover:text-red-600" />
            </button>
          </div>
          
          <p className="text-xs text-gray-400 mt-1">
            {format(message?.timestamp, 'h:mm a')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Bot className="w-5 h-5 text-[#4B3381]" />
            <h3 className="font-semibold text-gray-900">Ask Pastor</h3>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-gray-600">AI Online</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Powered by OpenAI • Grounded in Christ Faculty Content
        </p>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map(message => renderMessage(message))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 max-w-xs">
              <div className="flex items-center space-x-2">
                <Loader2 className="w-4 h-4 text-[#4B3381] animate-spin" />
                <span className="text-sm text-gray-600">Ask Pastor is thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      {/* Error Alert */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-200">
          <div className="flex items-center space-x-2 text-red-800">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}
      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e?.target?.value)}
            onKeyPress={(e) => {
              if (e?.key === 'Enter' && !e?.shiftKey) {
                e?.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Ask about faith, prayer, or biblical guidance..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4B3381] focus:border-transparent"
            disabled={isLoading}
          />
          <button 
            onClick={() => handleSendMessage()}
            disabled={!inputMessage?.trim() || isLoading}
            className="bg-[#4B3381] text-white px-4 py-2 rounded-lg hover:bg-[#4B3381]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
        
        {/* Quick Questions */}
        {messages?.length <= 1 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {quickQuestions?.map((question, index) => (
              <button 
                key={index}
                onClick={() => handleQuickQuestion(question)}
                disabled={isLoading}
                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded-full transition-colors disabled:opacity-50"
              >
                {question}
              </button>
            ))}
          </div>
        )}
        
        {/* Disclaimer */}
        <p className="text-xs text-gray-500 mt-2">
          Answers are generated from Christ Faculty content. For urgent pastoral care, contact our team directly.
        </p>
      </div>
    </div>
  );
};

AskPastorWidget.propTypes = {};

export default AskPastorWidget;